import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CriteriaForm from "@/components/CriteriaForm";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);
const mult = (v) => (v == null ? "—" : `${v % 1 === 0 ? v : v}×`);

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="bg-white p-8">
    <div className="font-mono text-4xl font-medium tracking-tighter text-ink md:text-5xl">{value}</div>
    <div className="mt-2 text-xs uppercase tracking-[0.15em] text-slate-500">{label}</div>
  </div>
);

export default function CriteriaDatabasePage() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/criteria/stats").then((r) => setData(r.data)).catch(() =>
      setData({ properties_reviewed: 0, county_comparison: [] }));
  }, []);

  useEffect(() => { load(); }, [load]);

  const has = data && data.properties_reviewed > 0;

  return (
    <div className="min-h-screen bg-white font-body text-ink">
      <Header />

      <section className="mx-auto max-w-7xl px-6 pb-12 pt-20 md:px-12 md:pt-28">
        <div className="text-xs uppercase tracking-[0.2em] text-calblue">Rental criteria database</div>
        <h1 className="mt-4 max-w-4xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-ink md:text-6xl">
          California Rental Criteria Database
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-700">
          A structured record of publicly advertised rental screening criteria across
          California. We document criteria — never companies. We publish only aggregate
          findings, and never show any individual listing.
        </p>
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
          <span className="h-2 w-2 animate-pulse rounded-full bg-calblue" />
          Every listing is verified by our research team before it is counted.
          <Link to="/methodology" className="ml-1 text-calblue underline-offset-2 hover:underline">Read our methodology</Link>
        </div>
      </section>

      {/* dashboard */}
      <section className="mx-auto max-w-7xl px-6 md:px-12">
        {data === null ? (
          <div className="py-16 text-center text-slate-400">Loading…</div>
        ) : !has ? (
          <div data-testid="criteria-empty" className="border border-slate-200 bg-[#F8FAFC] p-16 text-center">
            <div className="font-mono text-6xl font-medium tracking-tighter text-ink">0</div>
            <h3 className="mt-4 font-serif text-2xl font-medium text-ink">Collection has begun</h3>
            <p className="mx-auto mt-2 max-w-md text-slate-600">
              As verified listings accumulate, California's advertised screening criteria will
              appear here as aggregate statistics for researchers, journalists, and policymakers.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-px bg-slate-200 md:grid-cols-4">
              <Stat testid="cr-reviewed" label="Properties Reviewed" value={data.properties_reviewed.toLocaleString()} />
              <Stat testid="cr-rent" label="Median Rent" value={money(data.median_rent)} />
              <Stat testid="cr-credit" label="Median Credit Score" value={data.median_credit_score ?? "—"} />
              <Stat testid="cr-income" label="Median Income Requirement" value={mult(data.median_income_multiplier)} />
              <Stat testid="cr-4x" label="Requiring 4× or more" value={`${data.pct_requiring_4x_plus}%`} />
              <Stat testid="cr-cosign" label="Allowing Cosigners" value={`${data.pct_allowing_cosigners}%`} />
              <Stat testid="cr-reserves" label="Requiring Reserves" value={`${data.pct_requiring_reserves}%`} />
              <Stat testid="cr-fee" label="Avg. Application Fee" value={money(data.avg_application_fee)} />
            </div>

            {/* county comparison */}
            <div className="mt-16">
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">County comparison</h2>
              <div className="mt-6 overflow-x-auto border border-slate-200">
                <table className="w-full border-collapse text-sm" data-testid="county-table">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="px-5 py-3">County</th>
                      <th className="px-5 py-3">Listings</th>
                      <th className="px-5 py-3">Median Rent</th>
                      <th className="px-5 py-3">Median Credit</th>
                      <th className="px-5 py-3">Median Income</th>
                      <th className="px-5 py-3">4×+ Income</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.county_comparison.map((c) => (
                      <tr key={c.county} className="border-b border-slate-100">
                        <td className="px-5 py-3 font-medium">{c.county}</td>
                        <td className="px-5 py-3 font-mono">{c.count}</td>
                        <td className="px-5 py-3 font-mono">{money(c.median_rent)}</td>
                        <td className="px-5 py-3 font-mono">{c.median_credit ?? "—"}</td>
                        <td className="px-5 py-3 font-mono">{mult(c.median_income_multiplier)}</td>
                        <td className="px-5 py-3 font-mono">{c.pct_4x_plus}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </section>

      {/* contribute */}
      <section className="mt-24 border-t border-slate-200 bg-[#F8FAFC] px-6 py-24 md:px-12">
        <div className="mx-auto max-w-4xl">
          <div className="mb-10 text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-calblue">Contribute</div>
            <h2 className="mt-4 font-serif text-4xl font-medium tracking-tight text-ink md:text-5xl">
              Add a listing's advertised criteria
            </h2>
            <p className="mx-auto mt-4 max-w-xl leading-relaxed text-slate-600">
              Found a public rental listing? Record its advertised requirements below. Our
              research team verifies every entry before it counts toward the statistics.
            </p>
          </div>
          <CriteriaForm onSubmitted={load} />
        </div>
      </section>

      <section className="border-t border-slate-200 px-6 py-16 md:px-12">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6">
          <p className="text-slate-600">Curious how we collect and verify this data?</p>
          <Link to="/methodology" className="inline-flex items-center border border-ink px-6 py-3 text-sm font-medium text-ink transition-colors hover:bg-ink hover:text-white">
            Read the methodology <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
